import { inject } from '@angular/core';
import { CanDeactivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, Observable, shareReplay, take, tap } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { UnsavedChangesService } from 'src/app/services/unsaved-changes.service';

export const UNSAVED_TITLE_KEY = 'unsavedTitleKey';
export const UNSAVED_MESSAGE_KEY = 'unsavedMessageKey';

// Angular는 부모·자식 canDeactivate를 병렬 평가한다. 확인 응답 전에도
// 같은 이동의 스트림을 공유하고, 취소·완료·구독 해제 시 대기 항목을 비운다.
const pendingConfirmations = new WeakMap<UnsavedChangesService, {
  id: number;
  result: Observable<boolean>;
}>();

/** 화면 소스는 유지하고, 같은 이동에서 확인한 결과만 공유한다. */
export const unsavedChangesGuard: CanDeactivateFn<unknown> = (
  _component,
  currentRoute,
) => {
  const unsaved = inject(UnsavedChangesService);
  const router = inject(Router);
  const dialog = inject(DialogService);
  const translate = inject(TranslateService);

  if (!unsaved.isDirty()) return true;
  const navId = router.currentNavigation()?.id;
  if (unsaved.isConfirmed(navId)) return true;
  const pending = pendingConfirmations.get(unsaved);
  if (navId != null && pending?.id === navId) return pending.result;

  const data = currentRoute?.data;
  const messageKey = typeof data?.[UNSAVED_MESSAGE_KEY] === 'string'
    ? data[UNSAVED_MESSAGE_KEY] : 'CONFIRM.Unsaved_Leave';
  const titleKey = typeof data?.[UNSAVED_TITLE_KEY] === 'string'
    ? data[UNSAVED_TITLE_KEY] : 'CONFIRM.Unsaved_Leave_Title';

  const result = dialog.confirm(translate.instant(messageKey), translate.instant(titleKey)).pipe(
    take(1),
    map((ok) => ok === true),
    tap((ok) => {
      if (ok && navId != null) unsaved.markConfirmed(navId);
    }),
    finalize(() => {
      if (pendingConfirmations.get(unsaved)?.result === result) {
        pendingConfirmations.delete(unsaved);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  if (navId != null) pendingConfirmations.set(unsaved, { id: navId, result });
  return result;
};
